import { withMetrics } from "@expert/cloudwatch-metrics";
import { getLogger } from "@expert/logging";
import { HTTPError } from "ky";

import { VoiceTask } from "@/sdk/agent/voice/voiceTask";
import { getSharedApiClient } from "@/shared-utils";

import type { SurveyRequest, SurveyResponse } from "./types";

import { env } from "../environment";

const logger = getLogger({
    module: "sendSurveyData",
});

/**
 * @see https://ivr3.dev.rtt.contactcenter.npr.aws.asurion.net/docs#put-/api/v1/middletier/survey/data/soluto/-callId-
 */
export async function sendSurveyData(callId: string, requestBody: SurveyRequest): Promise<void> {
    try {
        await withMetrics(
            "sendSurveyData",
            async () => {
                try {
                    const data = await getSharedApiClient()
                        .put(`api/v1/middletier/survey/data/soluto/${callId}`, {
                            prefix: env.ivrApisBaseUrl,
                            json: { ...requestBody },
                        })
                        .json<SurveyResponse>();
                    logger.info({ data }, "Send survey data API call successful");
                    return data;
                } catch (error) {
                    if (!(error instanceof HTTPError)) throw error;
                    // ky 2 pre-reads the error body onto `data`; `error.response.text()` would reject (ew#7426).
                    logger.error({ error, errorText: error.data }, "Send survey data API call failed");
                    throw error;
                }
            },
            { logger, rethrow: false },
        );
    } catch (error: unknown) {
        // TODO: @asurion-core/expert-workspace-platform: refactor error handing.
        const status = error instanceof HTTPError ? error.response.status : undefined;
        logger.error({ error, statusCode: status }, "Unexpected error sending survey data");
    }
}

export const sendEmptySurveyData = async (
    task: VoiceTask,
    noSurveyReason: "IS_CALLBACK" | "CUSTOMER_DISCONNECTED" | "NOT_ELIGIBLE_FOR_SURVEY" | "AGENT_LEFT_CONFERENCE",
): Promise<void> => {
    logger.info({ noSurveyReason, task }, "Sending empty survey data");
    const payload: SurveyRequest & { noSurveyReason: string } = {
        customerCaseId: task.id,
        customerIdKind: "mdn",
        customerId: task.mdn,
        empId: "NONE",
        adName: "NONE",
        resolution: "NO_SURVEY",
        claimFiled: false,
        noSurveyReason,
    };

    await sendSurveyData(task.id, payload);
};
